/**
 * The full audit of what has happened on an item — the collapsed, opt-in
 * history (the raw label log + the /go plan reader). Extracted verbatim from
 * CaseFile (YAG-440) so the review case body renders the same disclosure.
 */
import { Fragment, useState } from "react";
import { ChevronDown } from "lucide-react";
import type { WorkItemActivityDto } from "@yagni/shared";
import { formatRelative } from "../lib/relativeTime.js";
import { Markdown } from "../components/Markdown.js";
import { chatActTitle, JOURNEY_FROM_CHAT } from "./workCopy.js";

export const HISTORY_LABELS: Record<string, string> = {
  comment: "Comment",
  agent_message: "@yagni",
  agent_question: "@yagni asked",
  phase_change: "Phase changed",
  assignee_change: "Assignee changed",
  description_updated: "Description updated",
  title_updated: "Title updated",
  trigger_started: "@yagni picked up the work",
  trigger_failed: "Trigger failed",
  session_started: "Coding session started",
  session_ended: "Coding session ended",
  plan_created: "Plan drafted",
  plan_confirmed: "Plan confirmed",
  gate_opened: "Gate opened",
  gate_resolved: "Gate resolved",
  plan_ready: "Plan ready",
  execution_started: "Execution started",
  result_ready: "Result ready",
  step_staged: "Step staged",
  step_executed: "Step shipped",
  step_failed: "Step failed",
  plan_recorded: "Plan recorded",
  run_outcome: "Run outcome",
  correction_captured: "Edit captured",
  accepted: "Accepted",
  chat_act: "From chat",
};

function historyLine(entry: WorkItemActivityDto): string {
  const base = HISTORY_LABELS[entry.type] ?? entry.type;
  if (entry.type === "phase_change") {
    const md = entry.metadata as { from?: string; to?: string } | null;
    return `${base}: ${md?.from ?? "?"} → ${md?.to ?? "?"}`;
  }
  if (entry.type === "trigger_failed") {
    const md = entry.metadata as { reason?: string } | null;
    return `${base}: ${md?.reason ?? "unknown"}`;
  }
  // The plan body is full markdown — too long for a one-line audit row. The
  // timeline is where you read the plan; here we just record that it happened.
  if (entry.type === "plan_recorded") return base;
  // An act from the ticket's chat: the act and that it came from the chat,
  // never what was said there.
  if (entry.type === "chat_act") {
    const title = chatActTitle(entry.metadata);
    return title ? `${title} · ${JOURNEY_FROM_CHAT}` : base;
  }
  if (entry.body) return `${base}: ${entry.body}`;
  return base;
}

/**
 * The rows alone, without the toggle — the review case body (YAG-440) mounts
 * these inside its own collapsible section, so a second toggle here would be
 * a control inside a control.
 */
export function HistoryList({ activity }: { activity: WorkItemActivityDto[] }) {
  // The recorded /go plan is full markdown — too tall for the one-line audit
  // rows — so it stays collapsed behind a per-row toggle. Track which plan beat
  // is expanded so the plan is actually READABLE here, not just logged.
  const [openPlanId, setOpenPlanId] = useState<string | null>(null);
  const newestFirst = [...activity].reverse();
  return (
    <div className="wk-hist-list">
      {newestFirst.map((entry) => {
        const isError = entry.type === "trigger_failed" || entry.type === "step_failed";
        const isPlan = entry.type === "plan_recorded" && !!entry.body?.trim();
        const planOpen = openPlanId === entry.id;
        return (
          <Fragment key={entry.id}>
            <div className={"wk-hist-row" + (isError ? " is-err" : "")}>
              <span className="wk-hist-dot" aria-hidden />
              {isPlan ? (
                <button
                  type="button"
                  className="wk-hist-plan-toggle"
                  onClick={() =>
                    setOpenPlanId((id) => (id === entry.id ? null : entry.id))
                  }
                  aria-expanded={planOpen}
                >
                  {entry.actorName ? `${entry.actorName} · ` : ""}
                  {historyLine(entry)}
                  <span className="wk-hist-plan-cue">{planOpen ? "Hide plan" : "Show plan"}</span>
                </button>
              ) : (
                <span className="wk-hist-text">
                  {entry.actorName ? `${entry.actorName} · ` : ""}
                  {historyLine(entry)}
                </span>
              )}
              <span className="wk-hist-when">{formatRelative(entry.createdAt)}</span>
            </div>
            {isPlan && planOpen && (
              <Markdown className="wk-hist-plan" text={entry.body ?? ""} />
            )}
          </Fragment>
        );
      })}
    </div>
  );
}

export function HistoryBlock({
  activity,
  loading,
}: {
  activity: WorkItemActivityDto[];
  loading: boolean;
}) {
  const [open, setOpen] = useState(false);
  if (loading || activity.length === 0) return null;
  return (
    <div className="wk-hist">
      <button
        type="button"
        className={"wk-archived-toggle" + (open ? " open" : "")}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <ChevronDown className="wk-did-chev" width={13} height={13} aria-hidden />
        History ({activity.length})
      </button>
      {open && <HistoryList activity={activity} />}
    </div>
  );
}
